import React, { CSSProperties } from "react";
import { NavItem } from "./types";

interface ExtraButtonExport {
  text: string;
  link: string;
  variant: "filled" | "outline" | "text";
  bgColor?: string;
  hideOnDesktop?: boolean;
  hideOnMobile?: boolean;
}

interface ExtraIconExport {
  iconUrl: string;
  link: string;
  bgColor?: string;
}

interface NavbarExportProps {
  logoText?: string | Record<string, string>;
  logoUrl?: string;
  navItems?: NavItem[];
  extraButtons?: ExtraButtonExport[];
  extraIcons?: ExtraIconExport[];
  backgroundColor?: string;
  textColor?: string;
  logoColor?: string;
  logoFontFamily?: string;
  sticky?: boolean;
  showCTA?: boolean;
  ctaText?: string | Record<string, string>;
  ctaLink?: string;
  ctaTarget?: "_self" | "_blank";
  ctaLinkType?: string;
  ctaBackgroundColor?: string;
  ctaHideOnDesktop?: boolean;
  ctaHideOnMobile?: boolean;
  maxWidth?: string;
  paddingX?: number;
  paddingY?: number;
  gap?: number;
  mobileBreakpoint?: number;
  navLayout?: "center" | "left" | "right";
  navFontSize?: number;
  navFontFamily?: string;
  showLangSelector?: boolean;
  languages?: string[];
}

export const NavbarExport: React.FC<NavbarExportProps> = ({
  logoText = "Clinic",
  logoUrl,
  navItems = [],
  extraButtons = [],
  extraIcons = [],
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  logoColor,
  logoFontFamily,
  sticky = true,
  showCTA = false,
  ctaText = "Book Now",
  ctaLink = "/contact",
  ctaTarget = "_self",
  ctaLinkType,
  ctaBackgroundColor,
  ctaHideOnDesktop = false,
  ctaHideOnMobile = false,
  maxWidth = "1280px",
  paddingX = 24,
  paddingY = 12,
  gap = 16,
  navLayout = "right",
  navFontSize,
  navFontFamily,
  showLangSelector = false,
  languages = ["TH", "EN"],
}) => {
  const safeNavItems = Array.isArray(navItems) ? navItems : [];
  const safeExtraButtons = Array.isArray(extraButtons) ? extraButtons : [];

  // Logo / CTA text may be a multilingual object ({th, en}) coming from a linked
  // Text/Button node. Resolve a display string and expose data-i18n attrs so the
  // runtime language switcher works (same pattern as nav item labels below).
  const resolveI18n = (val: string | Record<string, string> | undefined) => {
    if (val && typeof val === "object") {
      const obj = val as Record<string, string>;
      return { display: obj.th || obj.en || "", i18n: { "data-i18n-th": obj.th || "", "data-i18n-en": obj.en || "" } as Record<string, string> };
    }
    return { display: (val as string) || "", i18n: null as Record<string, string> | null };
  };
  const logo = resolveI18n(logoText);
  const cta = resolveI18n(ctaText);

  // Honor the CTA button's "open in new tab" setting. Internal (section) links stay _self.
  const ctaActualTarget = ctaLinkType && ctaLinkType !== "external" ? "_self" : ctaTarget || "_self";
  const ctaRel = ctaActualTarget === "_blank" ? "noopener noreferrer" : undefined;

  const navStyle: CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    paddingTop: `${paddingY}px`,
    paddingBottom: `${paddingY}px`,
    paddingLeft: `${paddingX}px`,
    paddingRight: `${paddingX}px`,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    backgroundColor,
    position: sticky ? "sticky" : "relative",
    top: sticky ? 0 : "auto",
    zIndex: 1000,
  };

  const isCenter = navLayout === "center";

  const innerStyle: CSSProperties = {
    maxWidth: maxWidth,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    gap: `${gap}px`,
  };

  const btnStyle = (btn: ExtraButtonExport): CSSProperties => ({
    padding: btn.variant === "text" ? "4px 8px" : "8px 16px",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "0.875rem",
    textDecoration: "none",
    whiteSpace: "nowrap",
    ...(btn.variant === "filled" && {
      color: "#ffffff",
      backgroundColor: btn.bgColor || textColor,
    }),
    ...(btn.variant === "outline" && {
      color: textColor,
      border: `1px solid ${textColor}`,
      backgroundColor: "transparent",
    }),
    ...(btn.variant === "text" && {
      color: textColor,
    }),
  });

  // Data attribute helpers for responsive hide
  const hideAttrs = (btn: { hideOnDesktop?: boolean; hideOnMobile?: boolean }) => ({
    ...(btn.hideOnDesktop ? { "data-hide-on-desktop": "true" } : {}),
    ...(btn.hideOnMobile ? { "data-hide-on-mobile": "true" } : {}),
  });

  const ctaHideAttrs = {
    ...(ctaHideOnDesktop ? { "data-hide-on-desktop": "true" } : {}),
    ...(ctaHideOnMobile ? { "data-hide-on-mobile": "true" } : {}),
  };

  const flagEmoji = (lang: string) => (lang === "TH" ? "\u{1F1F9}\u{1F1ED}" : "\u{1F1FA}\u{1F1F8}");

  const navLinkStyle: CSSProperties = {
    fontWeight: 500,
    fontSize: navFontSize ? `${navFontSize}px` : "0.9rem",
    ...(navFontFamily ? { fontFamily: `'${navFontFamily}', var(--font-family), sans-serif` } : {}),
    color: textColor,
    textDecoration: "none",
    whiteSpace: "nowrap",
    padding: "8px 16px",
    cursor: "pointer",
  };

  const normalizeHref = (href: string) => href === "/home" ? "/" : href;

  const hasThai = (s: string) => /[\u0E00-\u0E7F]/.test(s);
  const enFontStyle: CSSProperties = { fontFamily: "var(--font-family-en, var(--font-family))" };

  const navLinksEl = safeNavItems.map((item, index) => {
    if (typeof item.label === "object") {
      const obj = item.label as Record<string, string>;
      const displayText = obj.th || obj.en || "";
      return (
        <a key={index} href={normalizeHref(item.href)} className="nav-link i18n-text"
          style={{ ...navLinkStyle, ...(!hasThai(displayText) ? enFontStyle : {}) }}
          data-i18n-th={obj.th || ""} data-i18n-en={obj.en || ""}>{displayText}</a>
      );
    }
    const label = item.label as string;
    return (
      <a key={index} href={normalizeHref(item.href)} className="nav-link"
        style={{ ...navLinkStyle, ...(!hasThai(label) ? enFontStyle : {}) }}>{label}</a>
    );
  });

  const rightItemsEl = (
    <>
      {showLangSelector && languages.length > 0 && (
        <div className="navbar-lang-container" style={{ position: "relative", display: "inline-block" }}>
          <button className="navbar-lang-btn" style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", padding: "4px 8px", borderRadius: "4px", fontSize: "0.85rem", fontWeight: 500, color: textColor, background: "none", border: "none", fontFamily: "inherit" }}>
            {flagEmoji(languages[0])} {languages[0]} ▾
          </button>
          <div className="navbar-lang-dropdown" style={{ display: "none", position: "absolute", top: "100%", left: 0, marginTop: "4px", backgroundColor: "#ffffff", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", overflow: "hidden", zIndex: 1300, minWidth: "80px" }}>
            {languages.map((lang) => (
              <div key={lang} className="navbar-lang-option" data-lang={lang} style={{ padding: "8px 12px", fontSize: "0.85rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                {flagEmoji(lang)} {lang}
              </div>
            ))}
          </div>
        </div>
      )}
      {extraIcons.map((icon, index) => (
        <a
          key={`icon-${index}`}
          href={icon.link === "/line" ? "https://lin.ee/XoYhVRG" : icon.link}
          target={icon.link.startsWith("/") ? undefined : "_blank"}
          rel={icon.link.startsWith("/") ? undefined : "noopener noreferrer"}
          style={{ display: "flex", alignItems: "center", textDecoration: "none", ...(icon.bgColor ? { backgroundColor: icon.bgColor, borderRadius: "50%", padding: "4px" } : {}) }}
        >
          <img src={icon.iconUrl} alt="" style={{ height: "24px", width: "24px", objectFit: "contain" }} />
        </a>
      ))}
      {safeExtraButtons.map((btn, index) => (
        <a key={`extra-${index}`} href={btn.link} {...hideAttrs(btn)} style={{ ...btnStyle(btn), ...(!hasThai(btn.text) ? enFontStyle : {}) }}>{btn.text}</a>
      ))}
      {showCTA && (
        <a href={ctaLink} target={ctaActualTarget} rel={ctaRel} {...ctaHideAttrs} className={cta.i18n ? "i18n-text" : undefined} {...(cta.i18n || {})} style={{ marginLeft: "8px", color: "#ffffff", padding: "10px 20px", borderRadius: "8px", fontWeight: 600, fontSize: "0.875rem", textDecoration: "none", whiteSpace: "nowrap", backgroundColor: ctaBackgroundColor || "var(--color-primary, #3b82f6)", ...(!hasThai(cta.display) ? enFontStyle : {}) }}>
          {cta.display}
        </a>
      )}
    </>
  );

  // Logo element
  const logoFontStyle: CSSProperties = logoFontFamily
    ? { fontFamily: `'${logoFontFamily}', var(--font-family), sans-serif` }
    : !hasThai(logo.display) ? enFontStyle : {};
  const logoEl = (
    <a href="/" style={{ display: "flex", alignItems: "center", gap: "8px", textDecoration: "none", flexShrink: 0, color: textColor, ...(isCenter ? { flex: 1 } : {}) }}>
      {logoUrl && <img src={logoUrl} alt={logo.display} style={{ height: "40px", width: "auto" }} />}
      {logo.display && (
        <span
          className={logo.i18n ? "i18n-text" : undefined}
          {...(logo.i18n || {})}
          style={{ fontSize: "1.25rem", fontWeight: 700, ...(logoColor ? { color: logoColor } : {}), ...logoFontStyle }}
        >
          {logo.display}
        </span>
      )}
    </a>
  );

  return (
    <nav className="navbar" style={navStyle}>
      <div className="navbar-inner" style={innerStyle}>
        {logoEl}

        {isCenter ? (
          /* Center: 3 sections with flex:1 */
          <>
            <div className="navbar-desktop navbar-center-nav" style={{ display: "flex", alignItems: "center", gap: `${gap}px`, flex: 1, justifyContent: "center" }}>
              {navLinksEl}
            </div>
            <div className="navbar-desktop" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px", flex: 1, flexShrink: 0, flexWrap: "nowrap" }}>
              {rightItemsEl}
            </div>
          </>
        ) : (
          /* Right/Left: 1 combined group */
          <div className="navbar-desktop" style={{ display: "flex", alignItems: "center", gap: `${gap}px`, marginLeft: navLayout === "left" ? undefined : "auto", flexWrap: "nowrap" }}>
            {navLinksEl}
            {rightItemsEl}
          </div>
        )}
        <button
          className="navbar-mobile-btn"
          style={{
            display: "none",
            marginLeft: "auto",
            color: textColor,
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "8px",
          }}
        >
          <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      <div className="mobile-drawer">
        <div style={{ padding: "16px" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
            <button className="mobile-drawer-close" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px", background: "none", border: "none", cursor: "pointer", color: textColor }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {safeNavItems.map((item, index) => {
              if (typeof item.label === "object") {
                const obj = item.label as Record<string, string>;
                const displayText = obj.th || obj.en || "";
                const mobileStyle = { display: "block" as const, paddingTop: "12px", paddingBottom: "12px", fontWeight: 500, color: textColor, textDecoration: "none" as const, borderBottom: "1px solid #e5e7eb", ...(!hasThai(displayText) ? enFontStyle : {}) };
                return (
                  <a key={index} href={normalizeHref(item.href)} className="nav-link i18n-text"
                    style={mobileStyle}
                    data-i18n-th={obj.th || ""} data-i18n-en={obj.en || ""}>{displayText}</a>
                );
              }
              const label = item.label as string;
              return (
                <a key={index} href={normalizeHref(item.href)} className="nav-link"
                  style={{ display: "block", paddingTop: "12px", paddingBottom: "12px", fontWeight: 500, color: textColor, textDecoration: "none", borderBottom: "1px solid #e5e7eb", ...(!hasThai(label) ? enFontStyle : {}) }}>
                  {label}
                </a>
              );
            })}
            {safeExtraButtons
              .filter((btn) => !btn.hideOnMobile)
              .map((btn, index) => (
              <a
                key={`extra-m-${index}`}
                href={btn.link}
                style={{
                  display: "block",
                  textDecoration: "none",
                  ...(btn.variant === "text" && {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    fontWeight: 500,
                    color: textColor,
                    textAlign: "left" as const,
                    borderBottom: "1px solid #e5e7eb",
                  }),
                  ...(btn.variant === "outline" && {
                    padding: "8px 24px",
                    fontWeight: 600,
                    color: textColor,
                    border: `1.5px solid ${textColor}`,
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    textAlign: "center" as const,
                  }),
                  ...(btn.variant === "filled" && {
                    padding: "8px 24px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: btn.bgColor || textColor,
                    borderRadius: "8px",
                    textAlign: "center" as const,
                  }),
                }}
              >
                {btn.text}
              </a>
            ))}
            {showCTA && (
              <a
                href={ctaLink}
                target={ctaActualTarget}
                rel={ctaRel}
                {...ctaHideAttrs}
                className={cta.i18n ? "i18n-text" : undefined}
                {...(cta.i18n || {})}
                style={{
                  display: "block",
                  color: "#ffffff",
                  padding: "8px 24px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  textAlign: "center",
                  textDecoration: "none",
                  background: ctaBackgroundColor || "linear-gradient(135deg, var(--color-primary, #3b82f6) 0%, var(--color-secondary, #10b981) 100%)",
                }}
              >
                {cta.display}
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="mobile-overlay" />
    </nav>
  );
};
